var __assign = (this && this.__assign) || function () {
    __assign = Object.assign || function(t) {
        for (var s, i = 1, n = arguments.length; i < n; i++) {
            s = arguments[i];
            for (var p in s) if (Object.prototype.hasOwnProperty.call(s, p))
                t[p] = s[p];
        }
        return t;
    };
    return __assign.apply(this, arguments);
};
import React, { useCallback, useRef } from 'react';
import classNames from 'classnames';
import i18next from 'i18next';
import { useAppDispatch } from '~store';
import { OddPlateV2 } from '~molecules';
import { deleteOdd, selectOddWithValidation } from '~slices/shortcut';
import { useStructuredSelector } from '~hooks';
import { OddWidth } from '~constants/view/OddWidth';
import { LayoutOptions } from '~constants/view/LayoutOptions';
import { selectIdsMap } from '~slices/idsMap';
import { isOddActive } from '~utils/events';
import { useRowStyles } from '../Sidebets.styles';
import { RowWrapper } from './RowWrapper';
import { normalizeOneLineOc } from './normalizeOneLineOc';
import { getColumnsCount } from './getColumnsCount';
import { getOddWidth } from './getOddWidth';
import { normalizeMatrixOc } from './normalizeMatrixOc';
import { sortOutcomesByRows } from './sortOutcomesByRows';
var MemoizedOddPlateV2 = React.memo(OddPlateV2);
var Row = function (_a) {
    var oc = _a.oc, mk = _a.mk, eventId = _a.eventId, _b = _a.rowName, rowName = _b === void 0 ? '' : _b, tournamentId = _a.tournamentId, marketId = _a.marketId, eventsType = _a.eventsType, optionName = _a.optionName, defaultMarket = _a.defaultMarket;
    var classes = useRowStyles();
    var dispatch = useAppDispatch();
    var columnsCount = getColumnsCount(optionName);
    var idsMap = useStructuredSelector({
        idsMap: selectIdsMap,
    }).idsMap;
    var oddData = {
        dm: defaultMarket,
        lc: i18next.language,
        ei: eventId,
        mi: mk,
        ti: tournamentId,
        eventsType: eventsType,
        bmi: marketId,
    };
    var oddDataRef = useRef(oddData);
    oddDataRef.current = oddData;
    var handleSelect = useCallback(function (obid) {
        dispatch(selectOddWithValidation(__assign(__assign({}, oddDataRef.current), { obid: obid })));
    }, []);
    var handleRemove = useCallback(function (obid) {
        dispatch(deleteOdd(__assign(__assign({}, oddDataRef.current), { obid: obid })));
    }, []);
    var renderOdd = function (_a) {
        var _b;
        var outcome = _a.outcome, _c = _a.oddWidth, oddWidth = _c === void 0 ? OddWidth.M : _c, _d = _a.titled, titled = _d === void 0 ? false : _d, _e = _a.isShortName, isShortName = _e === void 0 ? false : _e;
        var nm = outcome.nm, snm = outcome.snm;
        var title = isShortName ? snm || nm || '' : nm || snm || '';
        return (React.createElement(MemoizedOddPlateV2, { value: (_b = outcome === null || outcome === void 0 ? void 0 : outcome.od) === null || _b === void 0 ? void 0 : _b.vl, title: titled ? title : '', oddWidth: oddWidth, key: outcome.id, isActive: isOddActive(outcome), isWidget: true, onSelect: handleSelect, onRemove: handleRemove, id: outcome.id, obid: outcome.bid, mk: mk, eventId: eventId }));
    };
    var renderEmptyOdd = function (key, oddWidth) {
        if (oddWidth === void 0) { oddWidth = OddWidth.M; }
        return React.createElement(MemoizedOddPlateV2, { key: key, oddWidth: oddWidth, isWidget: true });
    };
    var renderRow = function (_a) {
        var outcomes = _a.outcomes, _b = _a.name, name = _b === void 0 ? '' : _b, _c = _a.oddWidth, oddWidth = _c === void 0 ? OddWidth.M : _c, _d = _a.titledOdds, titledOdds = _d === void 0 ? false : _d, _e = _a.isShortName, isShortName = _e === void 0 ? false : _e;
        var firstOutcome = outcomes[0];
        var id = typeof firstOutcome === 'string' ? firstOutcome : firstOutcome.id;
        return (React.createElement(RowWrapper, { key: "".concat(optionName, "_").concat(marketId, "_").concat(id) },
            React.createElement("span", { className: classes.rowName }, name),
            React.createElement("div", { className: classes.rows }, outcomes.map(function (outcome) {
                return typeof outcome === 'string'
                    ? renderEmptyOdd(outcome, oddWidth)
                    : renderOdd({
                        outcome: outcome,
                        oddWidth: oddWidth,
                        titled: titledOdds,
                        isShortName: isShortName,
                    });
            }))));
    };
    var renderRowWithOddName = function (_a) {
        var outcome = _a.outcome, _b = _a.name, name = _b === void 0 ? '' : _b;
        return (React.createElement(RowWrapper, { key: "".concat(optionName, "_").concat(marketId, "_").concat(outcome.id) },
            React.createElement("div", { className: classNames(classes.rowField), key: outcome.id },
                React.createElement("span", { className: classNames(classes.rowName) }, name),
                React.createElement("div", { className: classNames(classes.oddNames) },
                    React.createElement("div", { className: classNames(classes.oddName), key: outcome.id }, outcome.nm)),
                React.createElement("div", { className: classes.rows }, renderOdd({ outcome: outcome })))));
    };
    switch (optionName) {
        case LayoutOptions.OPTION_1:
        case LayoutOptions.OPTION_2:
        case LayoutOptions.OPTION_3:
        case LayoutOptions.OPTION_4:
        case LayoutOptions.OPTION_10:
        case LayoutOptions.OPTION_11: {
            var outcomes = normalizeOneLineOc(oc, optionName, columnsCount, idsMap);
            var oddWidth = getOddWidth(optionName);
            var name_1 = rowName;
            return renderRow({ outcomes: outcomes, name: name_1, oddWidth: oddWidth, isShortName: true });
        }
        case LayoutOptions.OPTION_5: {
            var matrix = normalizeMatrixOc(oc, optionName, idsMap);
            var titledOdds_1 = true;
            return (React.createElement(React.Fragment, null, matrix.map(function (outcomes, i) {
                var name = i === 0 ? rowName : '';
                return renderRow({ outcomes: outcomes, name: name, titledOdds: titledOdds_1, isShortName: true });
            })));
        }
        case LayoutOptions.OPTION_6: {
            return (React.createElement(React.Fragment, null, sortOutcomesByRows(oc, optionName, idsMap).map(function (outcome, i) {
                var name = i === 0 ? rowName : '';
                return renderRowWithOddName({ outcome: outcome, name: name });
            })));
        }
        case LayoutOptions.OPTION_7: {
            return renderRow({ outcomes: oc.slice(0, 1), name: rowName });
        }
        default: {
            return null;
        }
    }
};
export { Row };
